
/**
 * Utilitários para otimização de busca textual
 */

// Função para normalização de texto
export const normalizeString = (str: string | null | undefined): string => {
  if (!str) return '';
  return str.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .trim();
};

// Tokeniza o termo de busca em palavras-chave
export const tokenizeSearchTerm = (term: string): string[] => {
  if (!term.trim()) return [];
  return term
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/\s+/)
    .filter(token => token.length > 2); // Ignora tokens muito pequenos
};

// Cache para valores normalizados para melhorar performance
const normalizedCache = new Map<string, string>();

// Normaliza e faz cache do resultado
export const getNormalizedValue = (value: string | null | undefined): string => {
  if (!value) return '';
  
  const cacheKey = `${value}`;
  if (normalizedCache.has(cacheKey)) {
    return normalizedCache.get(cacheKey) || '';
  }
  
  const normalized = normalizeString(value);
  normalizedCache.set(cacheKey, normalized);
  return normalized;
};

// Calcula similaridade entre duas strings (algoritmo de Levenshtein otimizado)
export const calculateSimilarity = (a: string, b: string): number => {
  if (a === b) return 1;
  if (!a || !b) return 0;

  const matrix = [];
  let i, j;

  // Initialize matrix
  for (i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Calculate Levenshtein distance
  for (i = 1; i <= b.length; i++) {
    for (j = 1; j <= a.length; j++) {
      const cost = a[j - 1] === b[i - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  // Convert distance to similarity score (0-1)
  const maxLength = Math.max(a.length, b.length);
  if (maxLength === 0) return 1;
  
  return 1 - (matrix[b.length][a.length] / maxLength);
};

// Define informações de searchable fields com pesos de relevância
export interface SearchableField {
  key: string;
  label: string;
  weight: number;
  isArray?: boolean;
}

// Os campos a serem pesquisados e seus pesos de relevância
export const SEARCHABLE_FIELDS: SearchableField[] = [
  { key: 'nome', label: 'Nome', weight: 100 },
  { key: 'email', label: 'E-mail', weight: 80 },
  { key: 'telefone', label: 'Telefone', weight: 70 },
  { key: 'cidade', label: 'Cidade', weight: 60 },
  { key: 'areas_atuacao', label: 'Áreas de Atuação', weight: 90, isArray: true },
  { key: 'areas', label: 'Áreas', weight: 85 },
  { key: 'bio', label: 'Bio', weight: 60 },
  { key: 'tipo_atuacao', label: 'Tipo de Atuação', weight: 50 },
  { key: 'objetivos', label: 'Objetivos', weight: 50 },
  { key: 'experiencias_anteriores', label: 'Experiências Anteriores', weight: 70 },
  { key: 'historia_profissional', label: 'História Profissional', weight: 65 },
  { key: 'pretensao_salarial', label: 'Pretensão Salarial', weight: 40 },
  { key: 'idiomas', label: 'Idiomas', weight: 50, isArray: true },
  { key: 'raca', label: 'Raça', weight: 30 },
];

// Calcula pontuação de relevância para correspondência direta
const calculateDirectMatchScore = (
  normalizedValue: string,
  searchTokens: string[],
  weight: number
): number => {
  if (!normalizedValue || searchTokens.length === 0) return 0;

  let score = 0;
  const lowerValue = normalizedValue.toLowerCase();

  for (const token of searchTokens) {
    // Correspondência exata com o campo
    if (lowerValue === token) {
      score += weight;
      continue;
    }

    // Início do campo
    if (lowerValue.startsWith(token)) {
      score += weight * 0.8;
      continue;
    }

    // Contém o token como palavra completa
    const regex = new RegExp(`\\b${token}\\b`, 'i');
    if (regex.test(lowerValue)) {
      score += weight * 0.7;
      continue;
    }

    // Contém o token em qualquer lugar
    if (lowerValue.includes(token)) {
      score += weight * 0.5;
      continue;
    }

    // Usa similaridade de string para correspondências parciais
    if (token.length > 3) {
      const similarity = calculateSimilarity(lowerValue, token);
      if (similarity > 0.7) {
        score += weight * 0.4 * similarity;
      }
    }
  }

  return score;
};

// Estrutura para armazenar o índice de pesquisa em memória
export interface SearchIndex<T> {
  documents: T[];
  normalizedValues: Map<string, Map<string, string>>;
  isIndexed: boolean;
}

// Cria um índice de busca para um conjunto de documentos
export function buildSearchIndex<T>(documents: T[]): SearchIndex<T> {
  const normalizedValues = new Map<string, Map<string, string>>();
  
  documents.forEach((doc, docIndex) => {
    const docMap = new Map<string, string>();
    
    SEARCHABLE_FIELDS.forEach(field => {
      const value = doc[field.key as keyof T];
      
      if (field.isArray && Array.isArray(value)) {
        // Para campos de array, normalize cada item e junte-os
        const normalizedArray = value
          .filter(item => item != null)
          .map(item => getNormalizedValue(String(item)))
          .join(' ');
        docMap.set(field.key, normalizedArray);
      } else if (value != null) {
        // Para campos regulares, normalize o valor
        docMap.set(field.key, getNormalizedValue(String(value)));
      }
    });
    
    normalizedValues.set(docIndex.toString(), docMap);
  });
  
  return {
    documents,
    normalizedValues,
    isIndexed: true
  };
}

// Interface para resultados de pesquisa pontuados
export interface ScoredResult<T> {
  item: T;
  score: number;
  matches: Map<string, number>;
}

// Executa pesquisa no índice com pontuação de relevância detalhada
export function searchInIndex<T>(
  searchTerm: string, 
  index: SearchIndex<T>,
  threshold: number = 1 // Limiar mínimo de pontuação
): ScoredResult<T>[] {
  if (!searchTerm.trim() || !index.isIndexed) {
    return index.documents.map(item => ({
      item,
      score: 0,
      matches: new Map()
    }));
  }
  
  const searchTokens = tokenizeSearchTerm(searchTerm);
  if (searchTokens.length === 0) {
    return index.documents.map(item => ({
      item,
      score: 0,
      matches: new Map()
    }));
  }
  
  const results: ScoredResult<T>[] = [];
  
  for (let i = 0; i < index.documents.length; i++) {
    const docId = i.toString();
    const docValues = index.normalizedValues.get(docId);
    const item = index.documents[i];
    
    if (!docValues) continue;
    
    let totalScore = 0;
    const fieldMatches = new Map<string, number>();
    
    SEARCHABLE_FIELDS.forEach(field => {
      const normalizedValue = docValues.get(field.key) || '';
      
      // Calcula pontuação para este campo
      const fieldScore = calculateDirectMatchScore(
        normalizedValue,
        searchTokens,
        field.weight
      );
      
      if (fieldScore > 0) {
        fieldMatches.set(field.key, fieldScore);
        totalScore += fieldScore;
      }
    });
    
    // Adiciona apenas resultados que atingem o limiar mínimo
    if (totalScore >= threshold) {
      results.push({
        item,
        score: totalScore,
        matches: fieldMatches
      });
    }
  }
  
  // Ordena por pontuação, do maior para o menor
  return results.sort((a, b) => b.score - a.score);
}
